import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useStore, productSubcat } from '../context/StoreContext';
import { Icons } from '../components/Icons';
import ProductCard from '../components/ProductCard';
import SmartImage from '../components/SmartImage';
import { contrastText } from '../utils/colorNames';
import { DETAIL_WIDTHS } from '../utils/imageUrl';
import { colorOptionsFor } from '../utils/productColors';
import { shopPath } from '../utils/slug';
import { trackWhatsApp, trackBeginCheckout } from '../utils/tracking';
import './ProductDetail.css';

export default function ProductDetail() {
  const {
    selectedProduct: p,
    relatedProducts,
    backFromProduct,
    goShop,
    addToCart,
    waNum,
    Rs,
  } = useStore();

  const [selectedColor, setSelectedColor] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);

  // The horizontal track the slides live in — swipe on touch, drag on
  // desktop, and arrows/dots/thumbnails all scroll it to the right slide.
  const trackRef = useRef(null);
  // Auto-play pauses while the shopper is touching, dragging or hovering.
  const pausedRef = useRef(false);

  // Scroll the track so slide `i` is in view.
  const goToSlide = useCallback((i) => {
    const track = trackRef.current;
    if (!track) return;
    const child = track.children[i];
    if (child) track.scrollTo({ left: child.offsetLeft, behavior: 'smooth' });
  }, []);

  // Keep the active dot/thumbnail in sync with whatever slide the shopper
  // has swiped to.
  const onTrackScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const i = Math.round(track.scrollLeft / track.clientWidth);
    setSlideIndex((prev) => (prev === i ? prev : i));
  }, []);

  // Desktop click-and-drag. Touch devices already swipe natively through
  // the scroll-snap track, so this only kicks in for a mouse.
  const drag = useRef({ active: false, startX: 0, startLeft: 0, moved: false });

  const onPointerDown = (e) => {
    if (e.pointerType === 'touch') return;      // let native touch scroll run
    const track = trackRef.current;
    if (!track) return;
    pausedRef.current = true;                    // stop auto-play while dragging
    drag.current = { active: true, startX: e.clientX, startLeft: track.scrollLeft, moved: false };
    track.classList.add('pd-slider--dragging');
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const track = trackRef.current;
    if (!track) return;
    const dx = e.clientX - d.startX;
    if (Math.abs(dx) > 3) d.moved = true;
    track.scrollLeft = d.startLeft - dx;
  };
  const endDrag = () => {
    const d = drag.current;
    if (!d.active) return;
    const track = trackRef.current;
    d.active = false;
    pausedRef.current = false;                   // resume auto-play after drag
    if (track) {
      track.classList.remove('pd-slider--dragging');
      // Snap to the nearest slide after a free drag.
      const i = Math.round(track.scrollLeft / track.clientWidth);
      goToSlide(i);
    }
  };

  // Jump back to the first photo whenever the shopper switches colour
  useEffect(() => { setSlideIndex(0); goToSlide(0); }, [selectedColor, goToSlide]);
  // …and whenever they land on a different product
  useEffect(() => { setSelectedColor(null); setSlideIndex(0); goToSlide(0); }, [p?.id, goToSlide]);

  // Auto-play removed — slider sirf user ke control par chalta hai:
  // arrow buttons (‹ ›), dots, thumbnails, swipe (mobile) aur drag (desktop).

  /* Card, detail page and admin all resolve colours the same way now. */
  const colorList = useMemo(() => colorOptionsFor(p), [p]);

  if (!p) {
    return (
      <div className="pd-page">
        <div className="pd-empty">
          {Icons.diamond(48)}
          <p>This product is no longer available.</p>
          <button className="btn-primary" onClick={backFromProduct}>Back to shop</button>
        </div>
      </div>
    );
  }

  const { name, cat, price, oldPrice, desc, badge, img, images, colorImages, dimensions, stock } = p;
  const sub = productSubcat(p);
  const cImgs = colorImages || {};

  // Every photo the shopper can see, in order and de-duplicated: the
  // product's general images first, then each colour's own photos. This is
  // the default slider — opening a product shows all its pictures at once
  // and the slider auto-plays through them.
  const generalSlides = (images && images.length > 0) ? images : (img ? [img] : []);
  const colorPhotos = colorList.flatMap((c) => cImgs[c.name] || []);
  const allSlides = Array.from(new Set([...generalSlides, ...colorPhotos]));

  // When the shopper taps a specific colour, narrow to just that colour's
  // photos; otherwise show every photo.
  const colorSlides = (selectedColor && cImgs[selectedColor.name] && cImgs[selectedColor.name].length)
    ? cImgs[selectedColor.name]
    : null;
  const baseSlides = allSlides.length ? allSlides : (img ? [img] : []);
  const slides = colorSlides || baseSlides;
  const curIndex = slideIndex < slides.length ? slideIndex : 0;

  const anyColorHasPhotos = Object.values(cImgs).some((arr) => arr && arr.length);
  const related = relatedProducts(p, 4);
  const outOfStock = typeof stock === 'number' && stock <= 0;

  const discount = oldPrice && oldPrice > price
    ? Math.round(((oldPrice - price) / oldPrice) * 100)
    : 0;

  /* The colour quoted in the order: whatever the shopper picked, or the
     colour the admin set for this product (its typed name, falling back to
     the detected colour only when nothing was set). */
  const orderColor = selectedColor
    || (colorList.length ? colorList[0] : null)
    || null;

  const prevSlide = () => goToSlide((curIndex - 1 + slides.length) % slides.length);
  const nextSlide = () => goToSlide((curIndex + 1) % slides.length);

  const SITE = 'https://cityshopingcenter.com';
  const productUrl = `${SITE}/product/${p.id}`;

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description: desc || `${name} — artificial ${String(cat).toLowerCase()} in American Diamond and zircon.`,
    image: slides.length ? slides : undefined,
    sku: p.id,
    category: sub ? `${cat} > ${sub}` : cat,
    brand: { '@type': 'Brand', name: 'City Shopping Center' },
    ...(orderColor ? { color: orderColor.name } : {}),
    ...(dimensions ? { size: dimensions } : {}),
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: 'PKR',
      price: String(price),
      availability: outOfStock
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: 'City Shopping Center & Dulha House' },
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Shop', item: `${SITE}/shop` },
      { '@type': 'ListItem', position: 2, name: cat, item: SITE + shopPath(cat) },
      ...(sub ? [{ '@type': 'ListItem', position: 3, name: sub, item: SITE + shopPath(cat, sub) }] : []),
      { '@type': 'ListItem', position: sub ? 4 : 3, name, item: productUrl },
    ],
  };

  const handleAdd = () => {
    addToCart(p.id, {
      color: orderColor ? { name: orderColor.name, hex: orderColor.hex } : null,
      size: dimensions || '',
      /* Jo picture is waqt slider par nazar aa rahi hai, wahi cart mein jaye —
         color ki pehli photo nahi. */
      img: slides[curIndex] || '',
    });
  };

  const buyOnWA = () => {
    trackWhatsApp('product_buy_now');
    trackBeginCheckout([{ id: p.id, name, cat, price, qty: 1 }], price);

    const rows = [
      `• ${name} — ${Rs(price)}`,
      `  Category: ${cat}${sub ? ' › ' + sub : ''}`,
    ];
    if (orderColor) rows.push(`  Color: ${orderColor.name} (${orderColor.hex})`);
    if (dimensions) rows.push(`  Size: ${dimensions}`);
    rows.push('  Quantity: 1');
    /* Current slide ki photo ka link — dukaandar ko exact design nazar aaye. */
    if (slides[curIndex]) rows.push(`  Photo: ${slides[curIndex]}`);

    const msg = `Assalam o Alaikum!\n\nMujhe ye product chahiye:\n\n${rows.join('\n')}\n\nPlease details bhej dein. Shukriya!`;
    window.open('https://wa.me/' + waNum + '?text=' + encodeURIComponent(msg), '_blank');
  };

  return (
    <div className="pd-page">
      <Helmet>
        <title>{`${name} — Buy ${cat} Online in Pakistan | City Shopping Center`}</title>
        {/* Product + Breadcrumb structured data: this is what lets Google
            show price and availability directly in the search result. */}
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <meta name="description" content={(desc || `${name} — premium artificial ${String(cat).toLowerCase()} in American Diamond & zircon. Buy online in Pakistan with 2-4 day delivery. City Shopping Center, Daska, Sialkot.`).slice(0, 160)} />
        <link rel="canonical" href={`https://cityshopingcenter.com/product/${p.id}`} />
        {slides[0] && <meta property="og:image" content={slides[0]} />}
      </Helmet>

      {/* Breadcrumb / back */}
      <div className="pd-bar">
        <button className="pd-back" onClick={backFromProduct}>
          <span className="pd-back-arrow">{Icons.arrow()}</span> Back
        </button>
        <nav className="pd-crumb" aria-label="Breadcrumb">
          <button onClick={() => goShop('All')}>Shop</button>
          <i aria-hidden="true">/</i>
          <button onClick={() => goShop(cat)}>{cat}</button>
          {sub && (
            <>
              <i aria-hidden="true">/</i>
              <button onClick={() => goShop(cat, sub)}>{sub}</button>
            </>
          )}
          <i aria-hidden="true">/</i>
          <b>{name}</b>
        </nav>
      </div>

      <div className="pd-main">
        {/* Gallery — a swipeable slider: swipe on touch, drag on desktop,
            with arrows, dots and thumbnails all driving the same track. */}
        <div className="pd-gallery">
          <div className="pd-image">
            <div
              className="pd-slider"
              ref={trackRef}
              onScroll={onTrackScroll}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerLeave={endDrag}
              onPointerCancel={endDrag}
            >
              {slides.map((url, i) => (
                /* key = url (na ke index): dusre design par jaane par React purana
                   <img> reuse nahi karta, warna pichli picture flash hoti thi. */
                <div className="pd-slide" key={url}>
                  <SmartImage
                    src={url}
                    alt={`${name}${colorSlides ? ' — ' + selectedColor.name : ''} ${i + 1}`}
                    className="pd-image__frame"
                    sizes="(max-width: 900px) 100vw, 560px"
                    widths={DETAIL_WIDTHS}
                    priority={i === 0}
                    fallback={<span className="pd-placeholder">{Icons.diamond(80)}</span>}
                  />
                </div>
              ))}
            </div>

            {colorSlides && <div className="pd-color-viewing">{selectedColor.name}</div>}
            {badge && <div className="pd-badge">{badge}</div>}
            {discount > 0 && <div className="pd-discount">-{discount}%</div>}

            {slides.length > 1 && (
              <>
                <button className="pd-slide-prev" onClick={prevSlide} aria-label="Previous image">‹</button>
                <button className="pd-slide-next" onClick={nextSlide} aria-label="Next image">›</button>

                <div className="pd-slide-dots">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      className={`pd-slide-dot${i === curIndex ? ' active' : ''}`}
                      onClick={() => goToSlide(i)}
                      aria-label={`Go to image ${i + 1}`}
                      aria-current={i === curIndex}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {slides.length > 1 && (
            <div className="pd-thumbs">
              {slides.map((url, i) => (
                <button
                  key={url}
                  className={`pd-thumb-btn ${i === curIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(i)}
                  aria-label={`Show image ${i + 1}`}
                >
                  <SmartImage src={url} alt="" sizes="90px" widths={[120, 200]} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="pd-info">
          <div className="pd-cat">
            {cat}{sub && <span className="pd-cat__sub"> · {sub}</span>}
          </div>
          <h1 className="pd-name">{name}</h1>

          <div className="pd-pricing">
            <span className="pd-price">{Rs(price)}</span>
            {oldPrice && oldPrice > price && <span className="pd-oldprice">{Rs(oldPrice)}</span>}
            {discount > 0 && <span className="pd-save">You save {Rs(oldPrice - price)}</span>}
          </div>

          {desc && <p className="pd-desc">{desc}</p>}

          {dimensions && (
            <div className="pd-spec">
              <span className="pd-spec__label">Size / dimensions</span>
              <span className="pd-spec__value">{dimensions}</span>
            </div>
          )}

          {colorList.length > 0 && (
            <div className="pd-colors-section">
              <div className="pd-colors-label">
                Colour
                {orderColor && (
                  <span className="pd-selected-color-name">
                    {orderColor.name}
                    <code>{orderColor.hex}</code>
                  </span>
                )}
              </div>

              <div className="pd-colors-row">
                {colorList.map((c) => {
                  const isSelected = orderColor && orderColor.name === c.name;
                  const shots = (cImgs[c.name] || []).length;
                  return (
                    <button
                      key={c.name}
                      className={`pd-color-btn${isSelected ? ' pd-color-active' : ''}${shots > 0 ? ' pd-color-has-photos' : ''}`}
                      style={{ background: c.hex, color: contrastText(c.hex) }}
                      title={shots > 0 ? `${c.name} — ${shots} photo${shots > 1 ? 's' : ''}` : `${c.name} — ${c.hex}`}
                      aria-label={c.name}
                      aria-pressed={Boolean(isSelected)}
                      onClick={() => setSelectedColor(isSelected ? null : c)}
                    />
                  );
                })}
              </div>

              <p className="pd-colors-hint">
                {anyColorHasPhotos
                  ? 'Tap a colour to see its photos. It is included in your order automatically.'
                  : colorList.length > 1
                    ? 'This colour is included in your order automatically.'
                    : `Available in ${colorList[0] ? colorList[0].name : 'this colour'}. Included in your order automatically.`}
              </p>
            </div>
          )}

          <ul className="pd-features">
            <li>{Icons.check(16)} Quality-checked before dispatch</li>
            <li>{Icons.check(16)} Free delivery on orders over Rs. 3,000</li>
          </ul>

          <div className="pd-actions">
            <button className="pd-add" onClick={handleAdd} disabled={outOfStock}>
              {Icons.cart(18)} {outOfStock ? 'Sold out' : 'Add to cart'}
            </button>
            <button className="pd-wa" onClick={buyOnWA}>
              {Icons.whatsapp(18)} Buy on WhatsApp
            </button>
          </div>

          <div className="pd-meta">
            <div className="pd-meta-item">{Icons.clock(15)} Ships in 2–4 days</div>
            <div className="pd-meta-item">{Icons.phone(15)} Help: +{waNum}</div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="pd-related">
          <div className="section-header center">
            <span className="section-tag">More to love</span>
            <h2 className="section-title">YOU MAY ALSO LIKE</h2>
          </div>
          <div className="products-grid">
            {related.map((rp) => (
              <ProductCard key={rp.id} product={rp} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
